import { useState } from 'react';
// @mui
import {
  Box,
  Stack,
  Typography,
  Radio,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import DIOText from '../../components/DIOText';
import FeeTaxDialog from "./FeeTaxDialog";

export default function NegotiationConditions({ offer }) {
  const smUp = useResponsive('up', 'sm');
  const [isOpenFeeTaxDialog, setIsOpenFeeTaxDialog] = useState(false);
  const planSelected = offer?.offer_status === 'accept';
  const plan4weeksSelected = planSelected && !offer?.nego_trial_selected && !offer?.nego_longterm_selected;
  const planTrialSelected = planSelected && offer?.nego_trial_selected;
  const planLongtermSelected = planSelected && offer?.nego_longterm_selected;
  const isResidentWork = offer?.work_type === "resident";

  return (
    <Stack spacing={3}>
      {/* without trial - old */}
      {!offer.nego_trial_salary && offer.nego_salary > 0 && (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            4주 인건비
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
          {offer.nego_salary}만원
        </Typography>
      </Stack>
      )}

      {!offer.nego_trial_working_hours_per_week && offer.nego_working_hours_per_week > 0 && (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            주당 업무 투입 시간
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
          주 {offer.nego_working_hours_per_week}시간
        </Typography>
      </Stack>
      )}

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_receipt.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            계약 플랜
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4 }} variant={smUp ? "body1" : "body2"}>
          {!planSelected && ('회사가 계약 플랜 중 하나를 선택하여 계약을 진행하게 됩니다.')}
          {plan4weeksSelected && ('회사가 4주 단위 계약 플랜을 선택했습니다. 계약은 4주마다 연장하며 진행합니다.')}
          {planTrialSelected && ('회사가 2주 체험판 플랜을 선택했습니다. 2주간 회사와 먼저 일해보고 계약을 계속 진행할지 회사가 결정하게 됩니다.')}
          {planLongtermSelected && ('회사가 8주 장기 계약 플랜을 선택했습니다. 이후 4주 단위로 계약을 연장하며 진행합니다.')}
        </Typography>
        <Stack sx={{ pl: 4, width: 1}} direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2}>
          {/* 4주 플랜 */}
          <Box
            sx={{
              width: 1,
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(145, 158, 171, 0.24)",
              ...plan4weeksSelected && {
                border: (theme) => `2px solid ${theme.palette.secondary.main}`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={smUp ? "h6" : "subtitle1"}>
                  4주 정식 계약
                </Typography>
                <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                  플랜{!planSelected && (' 적용시')}
                </Typography>
              </Stack>
              {planSelected && (<Radio color="secondary" value="4weeks" checked={plan4weeksSelected} />)}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }} paragraph>
              최종 제안한 4주 인건비와 주당 업무 시간이에요.
            </Typography>
            <Stack spacing={.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  인건비
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  {offer.nego_salary}만원
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (4주 기준)
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  주당 업무 가능한 시간
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  주 {offer.nego_working_hours_per_week}시간
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (4주 총 {4*offer.nego_working_hours_per_week}시간)
                </Typography>
              </Stack>
            </Stack>
          </Box>
          {/* 2주 플랜 */}
          {!isResidentWork && (
          <Box
            sx={{
              width: 1,
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(145, 158, 171, 0.24)",
              ...planTrialSelected && {
                border: (theme) => `2px solid ${theme.palette.secondary.main}`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={smUp ? "h6" : "subtitle1"}>
                  2주 단기 체험판
                </Typography>
                <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                  플랜{!planSelected && (' 적용시')}
                </Typography>
              </Stack>
              {planSelected && (<Radio color="secondary" value="trial" checked={planTrialSelected} />)}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              2주 동안만 일해보고, 정식 계약으로 전환할 수 있어요.
            </Typography>
            <Stack spacing={.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  인건비
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  {offer.nego_trial_salary}만원
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (2주 기준)
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  주당 업무 가능한 시간
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  주 {offer.nego_trial_working_hours_per_week}시간
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (2주 총 {2*offer.nego_trial_working_hours_per_week}시간)
                </Typography>
              </Stack>
            </Stack>
          </Box>
          )}
          {/* 8주 플랜 */}
          <Box
            sx={{
              width: 1,
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(145, 158, 171, 0.24)",
              ...planLongtermSelected && {
                border: (theme) => `2px solid ${theme.palette.secondary.main}`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={smUp ? "h6" : "subtitle1"}>
                  8주 장기 계약
                </Typography>
                <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                  플랜{!planSelected && (' 적용시')}
                </Typography>
              </Stack>
              {planSelected && (<Radio color="secondary" value="longterm" checked={planLongtermSelected} />)}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              업무 내용에 따라 장기 계약할 수 있어요.
            </Typography>
            <Stack spacing={.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  인건비
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  {offer.nego_longterm_salary}만원
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (8주 기준)
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                <Typography variant="subtitle1">
                  주당 업무 가능한 시간
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                  주 {offer.nego_longterm_working_hours_per_week}시간
                </Typography>
                <Typography sx={{ color: "text.secondary" }} variant="body2">
                  (8주 총 {8*offer.nego_longterm_working_hours_per_week}시간)
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
        <Box sx={{ pl: 4 }}>
          <Button
            variant="text"
            size="small"
            sx={{
              maxWidth: 'max-content',
              color: 'text.secondary',
            }}
            fullWidth={false}
            startIcon={<Iconify icon={'eva:info-fill'} />}
            onClick={() => setIsOpenFeeTaxDialog(true)}
          > 
            수수료 & 세금 정책
            </Button>
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            업무 시작 가능일
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
          {offer.nego_start_date} 이후
        </Typography>
      </Stack>

      {offer.nego_core_time && Array.isArray(offer.nego_core_time) && offer.nego_core_time.length > 0 && (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/icon-chat-bubble-dots-mono.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            코어 타임 (한국 시간 KST 기준)
          </Typography>
        </Stack>
        <Stack>
        {offer.nego_core_time?.map((t, index) => (
          <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1" key={index}>
            {t.day} {t.start.label} ~ {t.end.label}
          </Typography>
        ))}
        </Stack>
      </Stack>
      )}

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="h6">
            업무 내용
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
          {offer.nego_task}
        </Typography>
      </Stack>

      {/* 정책 안내 box */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderRadius: 1.5,
          backgroundColor: "grey.100",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          정책 안내
        </Typography>
        <Stack direction={"column"} spacing={{ xs: 2, sm: 3 }} sx={{ mb: 1 }} >
          {/* 급여 정책 */}
          <Stack sx={{ width: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
              <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: offer?.is_account_monitored ? "text.primary" : "info.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
              <Typography variant="subtitle2" gutterBottom>
                급여 지급 정책
              </Typography>
            </Stack>
            <Typography variant="overline" sx={{ mb: .5 }}>
              <DIOText /> 급여청구서 기능으로 청구한 급여만 지급됩니다.
            </Typography>
            <ul style={{ marginLeft: "16px" }}>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  각 주차의 마지막날 자정까지 해당 주차의 급여를 청구해야합니다.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  기한 내에 청구하지 않으면, 기록된 근무 시간으로 자동 청구됩니다.<br/>
                  (근무 시간이 계약 시간을 넘을 경우, 계약 시간만큼만 청구됩니다.)
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  기록된 근무 시간 보다 더 많이 청구하는 경우 사유를 함께 제출해야합니다.
                </Typography>
              </li>
            </ul>
          </Stack>
          {/* 출퇴근 정책 */}
          <Stack sx={{ width: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
              <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: offer?.is_account_monitored ? "text.primary" : "oldprimary.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
              <Typography variant="subtitle2" gutterBottom>
                출퇴근 정책
              </Typography>
            </Stack>
            <Typography variant="overline" sx={{ mb: .5 }}>
              회사와 계약 기간 동안 <DIOText /> 시스템 내 출퇴근 기능으로 업무를 기록해야합니다.
            </Typography>
            <ul style={{ marginLeft: "16px" }}>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  약속한 코어 타임에 근무해야하며, 출퇴근 일정 변경 시 회사에 사전 공유해주세요.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  코어타임 반복 미준수 시 기업이 업무 불성실로 간주하여 이의 제기 또는 계약 해지의 사유로 삼을 수 있으니 유의하여 주십시오.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  출퇴근 기록과 당일 진행한 업무는 실시간으로 <DIOText />와 회사에 공유됩니다.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  출근 시 해야할 업무를 기록해야 출근 기록이 시작되며, 퇴근 시 진행한 업무를 기록해야 퇴근 시간이 기록됩니다.
                </Typography>
              </li>
            </ul>
          </Stack>
          {/* 업무 계정 정책 */}
          {offer?.is_account_monitored && (
          <Stack sx={{ width: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
              <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "info.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
              <Typography variant="subtitle2" gutterBottom>
                업무 계정 지급 및 활동 모니터링 안내
              </Typography>
            </Stack>
            <Typography variant="overline" sx={{ mb: .5 }}>
              <DIOText />에서 지급한 이메일 계정을 통해 진행된 작업만이 공식 업무로 인정되며, <DIOText />는 지급한 업무용 이메일 계정을 통해 이루어진 활동을 업무 효율 향상 및 품질 관리를 위해 열람/기록/분석할 수 있습니다.
            </Typography>
            <ul style={{ marginLeft: "16px" }}>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  <DIOText />는 효율적인 협업과 업무 관리 목적을 위해, 모든 크루에게 업무용 이메일 계정을 제공합니다.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  해당 계정은 Notion, Slack, Figma, Github, Gmail 등 모든 업무 툴에 연동되어야 하며, 이를 통해 업무 로그 및 협업 활동이 기록됩니다.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  해당 계정을 통해 진행된 작업만이 공식 업무로 인정됩니다.
                </Typography>
              </li>
              <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                <Typography variant="caption">
                  <DIOText />는 지급한 업무용 이메일 계정을 통해 이루어진 활동을 업무 효율 향상 및 품질 관리를 위해 열람/기록/분석할 수 있습니다.
                </Typography>
              </li>
            </ul>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={true} readOnly />}
                label="네. 동의합니다."
              />
            </FormGroup>
          </Stack>
          )}
        </Stack>
      </Box>

      <FeeTaxDialog isOpen={isOpenFeeTaxDialog} close={() => setIsOpenFeeTaxDialog(false)} />
    </Stack>
  )
}